import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Search, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useCandidates } from "@/hooks/candidates/useCandidates";
import { useUploadTranscript } from "@/hooks/transcripts/useTranscriptMutations";

const ACCEPTED_EXTENSIONS = [".vtt", ".txt", ".srt"];
const MAX_FILE_BYTES = 2 * 1024 * 1024;

export interface TranscriptUploadDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Pre-linked candidate, e.g. when uploading from a candidate's tab. */
	defaultCandidate?: { id: string; name: string };
	onUploaded?: (transcriptId: string) => void;
}

/** Manual transcript upload: file validation, subject, date, candidate link. */
export const TranscriptUploadDialog: React.FC<TranscriptUploadDialogProps> = ({
	open,
	onOpenChange,
	defaultCandidate,
	onUploaded,
}) => {
	const { t } = useTranslation();
	const { candidates: allCandidates } = useCandidates();
	const uploadMutation = useUploadTranscript();

	const [file, setFile] = useState<File | null>(null);
	const [subject, setSubject] = useState("");
	const [date, setDate] = useState("");
	const [fileError, setFileError] = useState<string | null>(null);
	const [candidate, setCandidate] = useState<{ id: string; name: string } | null>(defaultCandidate || null);
	const [candidateQuery, setCandidateQuery] = useState("");

	const candidateResults = useMemo(() => {
		if (!candidateQuery.trim())
			return [];
		const words = candidateQuery.toLowerCase().trim().split(/\s+/);
		return (allCandidates || [])
			.filter((c) => words.every((word) => (c.name || "").toLowerCase().includes(word)))
			.slice(0, 5)
			.map((c) => ({ id: c.id, name: c.name || "" }));
	}, [allCandidates, candidateQuery]);

	const reset = () => {
		setFile(null);
		setSubject("");
		setDate("");
		setFileError(null);
		setCandidate(defaultCandidate || null);
		setCandidateQuery("");
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen)
			reset();
		onOpenChange(nextOpen);
	};

	const handleFileChange = (selected: File | null) => {
		setFileError(null);
		if (!selected) {
			setFile(null);
			return;
		}
		const extension = selected.name.slice(selected.name.lastIndexOf(".")).toLowerCase();
		if (!ACCEPTED_EXTENSIONS.includes(extension)) {
			setFileError(t("transcripts.fileTypeInvalid", "Unsupported file type. Use .vtt, .txt or .srt."));
			setFile(null);
			return;
		}
		if (selected.size > MAX_FILE_BYTES) {
			setFileError(t("transcripts.fileTooLarge", "File is too large (max 2 MB)."));
			setFile(null);
			return;
		}
		setFile(selected);
	};

	const handleUpload = async () => {
		if (!file)
			return;
		const content = await file.text();
		const isVtt = file.name.toLowerCase().endsWith(".vtt");
		uploadMutation.mutate(
			{
				subject:
					subject ||
					file.name.replace(/\.(vtt|txt|srt)$/i, "") ||
					t("transcripts.uploadedTranscript", "Uploaded transcript"),
				content,
				content_type: isVtt ? "vtt" : "text",
				start_time: date ? new Date(date).toISOString() : undefined,
				candidate_id: candidate?.id,
			},
			{
				onSuccess: (data) => {
					handleOpenChange(false);
					if (data.id)
						onUploaded?.(data.id);
				},
			}
		);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("transcripts.uploadTitle", "Upload transcript")}</DialogTitle>
					<DialogDescription>
						{t("transcripts.uploadDescription", "Add a meeting transcript from a file.")}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 pt-2">
					<div className="space-y-2">
						<Label htmlFor="transcript-upload-file">{t("transcripts.file", "File")}</Label>
						<Input
							id="transcript-upload-file"
							type="file"
							accept={ACCEPTED_EXTENSIONS.join(",")}
							onChange={(event) => handleFileChange(event.target.files?.[0] || null)}
							className="cursor-pointer"
						/>
						{fileError ? (
							<p className="text-xs text-destructive">{fileError}</p>
						) : (
							<p className="text-xs text-muted-foreground">
								{t("transcripts.fileHint", "Supported formats: .vtt, .txt, .srt (max 2 MB)")}
							</p>
						)}
					</div>
					<div className="space-y-2">
						<Label htmlFor="transcript-upload-subject">{t("transcripts.subject", "Subject")}</Label>
						<Input
							id="transcript-upload-subject"
							placeholder={t("transcripts.subjectPlaceholder", "Meeting subject (optional)")}
							value={subject}
							onChange={(event) => setSubject(event.target.value)}
							maxLength={500}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="transcript-upload-date">{t("transcripts.date", "Date")}</Label>
						<Input
							id="transcript-upload-date"
							type="datetime-local"
							value={date}
							onChange={(event) => setDate(event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="transcript-upload-candidate">
							{t("transcripts.linkToCandidate", "Link to candidate (optional)")}
						</Label>
						{candidate ? (
							<div className="flex items-center justify-between rounded-sm border border-border bg-muted/30 px-3 py-2 text-sm">
								<span className="truncate">{candidate.name}</span>
								{!defaultCandidate && (
									<button
										type="button"
										onClick={() => setCandidate(null)}
										className="text-muted-foreground transition-colors hover:text-foreground"
										aria-label={t("common.clear", "Clear")}
									>
										<X className="h-3.5 w-3.5" />
									</button>
								)}
							</div>
						) : (
							<div className="relative">
								<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id="transcript-upload-candidate"
									value={candidateQuery}
									onChange={(event) => setCandidateQuery(event.target.value)}
									placeholder={t("tasks.relations.searchCandidates", "Search candidates...")}
									className="h-9 pl-8"
								/>
								{candidateResults.length > 0 && (
									<ul className="absolute z-10 mt-1 w-full space-y-0.5 rounded-sm border border-border bg-popover p-1 shadow-card">
										{candidateResults.map((result) => (
											<li key={result.id}>
												<button
													type="button"
													className="w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
													onClick={() => {
														setCandidate(result);
														setCandidateQuery("");
													}}
												>
													{result.name}
												</button>
											</li>
										))}
									</ul>
								)}
							</div>
						)}
					</div>
					<div className="flex justify-end gap-2 pt-2">
						<Button variant="outline" onClick={() => handleOpenChange(false)}>
							{t("common.cancel", "Cancel")}
						</Button>
						<Button onClick={handleUpload} disabled={!file || uploadMutation.isPending} className="gap-2">
							{uploadMutation.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Upload className="h-4 w-4" />
							)}
							{uploadMutation.isPending
								? t("transcripts.uploading", "Uploading…")
								: t("transcripts.upload", "Upload")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
